/**
 * RP4 — Custom hook for the renderer-handle + test-bridge install.
 *
 * Combines the once-per-mount `makePlaceholderHandle` + `BatchedLayer`
 * + `installSceneTestBridge` setup into a single hook the orchestrator
 * can invoke without inlining the effect. The pattern texture cache
 * is also released on unmount.
 *
 * Bridge wiring: the projection helper is built from caller-supplied
 * `cameraRef` / `canvasRef` refs so `__edit_dwg_scene.cameraZoom() /
 * cameraPosition() / cameraWorldRect() / worldFromClient() /
 * clientFromWorld()` all return live `THREE.OrthographicCamera` +
 * canvas state. Both refs are populated by `Scene3DStage` once R3F
 * mounts (`InitialCameraMount` for the camera, `<Canvas onCreated>`
 * for the canvas element), so the projection lazily reads `.current`
 * on each call — no race with mount ordering.
 */

import { useEffect, useRef, type MutableRefObject } from "react";
import type * as THREE from "three";

import type { RendererHandle } from "../types";
import { BatchedLayer } from "../batchedLayer";
import {
  installSceneTestBridge,
  stubSceneBridgeStore,
  type SceneBridgeProjection,
} from "../testBridge";
import { clearPatternTextureCache } from "../hatch/hatchPatterns";
import { clearLinetypePatternTextureCache } from "../rendering/linetypePatternTexture";
import { applyOffset, getCoordinateOrigin, unapplyOffset } from "../coordinateOrigin";
import {
  clientToWorld,
  worldToClient,
  pickApertureWorldTolerance,
  PICK_APERTURE_PX,
} from "../stageCameraMath";
import { makePlaceholderHandle } from "./scenePlaceholder";

export interface StageRendererHandleRefs {
  handleRef: MutableRefObject<RendererHandle | null>;
  batchedLayerRef: MutableRefObject<BatchedLayer | null>;
}

export interface UseStageRendererHandleArgs {
  /**
   * Camera ref populated by `InitialCameraMount` once R3F has the
   * `OrthographicCamera` instance available. Null until the first
   * useLayoutEffect inside `<Canvas>` runs.
   */
  cameraRef: MutableRefObject<THREE.OrthographicCamera | null>;
  /**
   * Canvas ref populated by `<Canvas onCreated>` once R3F has created
   * the `WebGLRenderer` (and therefore its `domElement`). Null until
   * `onCreated` fires — before that, `worldFromClient` / `clientFromWorld`
   * have no canvas rect to project against.
   */
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
}

/**
 * Build a `SceneBridgeProjection` whose accessors read live state from
 * `cameraRef.current` / `canvasRef.current`. Called once per mount; the
 * closures dereference `.current` lazily on each call so probes never
 * see a stale snapshot.
 *
 * `worldFromClient` / `clientFromWorld` run the SAME camera math the
 * production pointer pipeline uses (`stageCameraMath.clientToWorld` /
 * its exact inverse `worldToClient`), so `__edit_dwg_scene.entityBounds()`
 * / `entityIdAt()` see the identical client-pixel <-> world mapping a
 * real click does — not the identity pass-through this hook shipped
 * with previously (PR-C TODO from commit ba2271685, never wired: those
 * two accessors echoed their input straight back, so `entityBounds()`
 * silently returned raw world units under any non-identity camera even
 * though its doc contract promises client-space pixels).
 *
 * Every accessor re-adds/subtracts the `coordinateOrigin` offset so
 * callers receive AutoCAD-world coords on the world side (the renderer
 * keeps Float32-precision-safe offset-world coords internally) — the
 * same convention `cameraWorldRect` / `cameraPosition` already used.
 */
export function buildProjection(
  cameraRef: MutableRefObject<THREE.OrthographicCamera | null>,
  canvasRef: MutableRefObject<HTMLCanvasElement | null>,
): SceneBridgeProjection {
  // Closures capture `cameraRef` / `canvasRef` (stable identity from the
  // caller's useRef). Each accessor reads `.current` lazily, so probes
  // never see a stale snapshot — the refs are populated once R3F finishes
  // mounting the Canvas.
  const readCam = () => cameraRef.current;
  const readCanvas = () => canvasRef.current;
  /**
   * Camera for the two PROJECTING accessors, with its world matrices
   * brought up to date first.
   *
   * #589 Codex round 2 — `clientToWorld` / `worldToClient` project via
   * THREE's `unproject()` / `project()`, which read `matrixWorld` and
   * `matrixWorldInverse`. Camera mutations that happen OUTSIDE the render
   * loop refresh `projectionMatrix` but NOT `matrixWorld`:
   * `fitOrthographicCameraToRenderables` (import auto-fit / ZOOM E) ends on
   * `lookAt()`, and `applySavedViewToOrthographicCamera` sets `position` —
   * both call only `updateProjectionMatrix()` (`sceneBulkLoadHelpers.ts`
   * :1031, :1121). three.js normally refreshes `matrixWorld` once per frame
   * inside the renderer, but a bridge probe runs SYNCHRONOUSLY, in the same
   * task as the mutation (a canary reading `entityBounds()` right after
   * file-open / `ZOOM E` / a saved view), so it would otherwise project
   * against the PREVIOUS camera pose until the next tick.
   *
   * `THREE.Camera.updateMatrixWorld()` also re-inverts `matrixWorldInverse`,
   * so this one call covers both projection directions. It is idempotent and
   * effectively free when nothing is dirty. Deliberately applied HERE rather
   * than inside `stageCameraMath` — production's pointer path already runs
   * after a render tick, and this keeps that hot path byte-identical.
   */
  const readProjectionCam = () => {
    const cam = cameraRef.current;
    cam?.updateMatrixWorld();
    return cam;
  };
  const readOrigin = () => getCoordinateOrigin();
  return {
    worldFromClient: (clientX, clientY) => {
      const cam = readProjectionCam();
      const canvas = readCanvas();
      // Pre-mount degenerate case: the bridge can be queried before R3F
      // has attached a camera/canvas. There is no meaningful projection
      // yet, so echo the input back rather than throw — matching the
      // other accessors' stub-default convention below (cameraZoom() ->
      // 0, cameraWorldRect() -> {0,0,0,0}). Once mounted this branch is
      // never taken again for the lifetime of the stage.
      if (!cam || !canvas) return { x: clientX, y: clientY };
      const renderSpace = clientToWorld(clientX, clientY, canvas, cam);
      const world = unapplyOffset({
        x: renderSpace.x,
        y: renderSpace.y,
        z: 0,
      });
      return { x: world.x, y: world.y };
    },
    clientFromWorld: (world) => {
      const cam = readProjectionCam();
      const canvas = readCanvas();
      if (!cam || !canvas) return { x: world.x, y: world.y };
      const renderSpace = applyOffset({ x: world.x, y: world.y, z: 0 });
      return worldToClient(renderSpace.x, renderSpace.y, canvas, cam);
    },
    cameraWorldRect: () => {
      const cam = readCam();
      if (!cam) return { left: 0, top: 0, right: 0, bottom: 0 };
      const zoom = cam.zoom || 1;
      const origin = readOrigin();
      const ox = origin?.x ?? 0;
      const oy = origin?.y ?? 0;
      return {
        left: cam.position.x + cam.left / zoom + ox,
        right: cam.position.x + cam.right / zoom + ox,
        top: cam.position.y + cam.top / zoom + oy,
        bottom: cam.position.y + cam.bottom / zoom + oy,
      };
    },
    cameraZoom: () => readCam()?.zoom ?? 0,
    cameraPosition: () => {
      const cam = readCam();
      if (!cam) return { x: 0, y: 0 };
      const origin = readOrigin();
      return {
        x: cam.position.x + (origin?.x ?? 0),
        y: cam.position.y + (origin?.y ?? 0),
      };
    },
    worldTolerance: () => {
      const cam = readCam();
      const canvas = readCanvas();
      // Pre-mount fallback (kept from the pre-#589-fix formula): no
      // canvas rect to size an aperture against yet.
      if (!cam || !canvas) return 1 / Math.max(cam?.zoom ?? 1, 1e-6);
      // #589 Codex review — this used to be a bare `1/zoom`, which ignores
      // canvas size and frustum width entirely. After a fit-to-extents on
      // a large drawing, `zoom` is often ~1 while the frustum spans
      // thousands of world units, so `1/zoom` searched a ~1-world-unit box
      // where a real click's 4-CSS-px aperture (PICK_APERTURE_PX, the same
      // constant `StagePointerHandlers.ts` hit-tests against) would cover
      // far more. Matching that aperture means entityIdAt() finds what a
      // real click would.
      const rect = canvas.getBoundingClientRect();
      return pickApertureWorldTolerance(cam, rect.width, PICK_APERTURE_PX);
    },
  };
}

/**
 * Mount + dispose the placeholder renderer handle and BatchedLayer for
 * the current Scene3DStage instance. Test bridge is installed alongside
 * with a camera-aware projection so e2e probes can read scene state.
 */
export function useStageRendererHandle(
  args: UseStageRendererHandleArgs,
): StageRendererHandleRefs {
  const handleRef = useRef<RendererHandle | null>(null);
  const batchedLayerRef = useRef<BatchedLayer | null>(null);
  const { cameraRef, canvasRef } = args;
  // Stash on refs so the install effect deps stay empty — `cameraRef` /
  // `canvasRef` are useRefs from the parent (stable identity) but
  // destructuring from a new `args` object each render makes the
  // value-vs-reference distinction non-obvious; keeping the install
  // effect on `[]` makes the install-once contract explicit.
  const cameraRefStash = useRef(cameraRef);
  cameraRefStash.current = cameraRef;
  const canvasRefStash = useRef(canvasRef);
  canvasRefStash.current = canvasRef;
  useEffect(() => {
    const placeholder: RendererHandle = makePlaceholderHandle();
    handleRef.current = placeholder;
    batchedLayerRef.current = new BatchedLayer();
    const projection = buildProjection(
      cameraRefStash.current,
      canvasRefStash.current,
    );
    const teardown = installSceneTestBridge(
      placeholder,
      stubSceneBridgeStore,
      projection,
    );
    return () => {
      teardown();
      placeholder.dispose();
      // PR-D — drop pattern textures alongside the renderer handle so
      // GPU memory is reclaimed on hot-reload / unmount.
      clearPatternTextureCache();
      // PR-LT3 A1 — same lifecycle for the multi-element linetype pattern
      // textures (module-scope cache, mirrors the hatch cache).
      clearLinetypePatternTextureCache();
      handleRef.current = null;
      batchedLayerRef.current = null;
    };
  }, []);
  return { handleRef, batchedLayerRef };
}
